import React from 'react';

import styled from '@emotion/styled';

import { useMediaQuery } from '@zego/hooks/useMediaQuery';
import { BREAKPOINTS, primaryColor, unit, unitTRBL } from '@zego/theme';

import View from '@zego/components/atoms/View';
import GetAQuoteButton from '@zego/components/molecules/GetAQuoteButton';
import StickyBanner from '@zego/components/molecules/StickyBanner';
import FleetBrokerPartnerWithUsButton from '@zego/components/organisms/FleetBrokerJoinOurNetworkButton';

import ContentContainer from '../ContentContainer';

const Container = styled(ContentContainer)({
  [BREAKPOINTS.medium]: {
    flexDirection: 'row',
  },

  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',

  padding: unitTRBL(2, 4, 2, 4),
  width: '100%',
});

const Heading = styled.h3({
  color: primaryColor(2),
  fontSize: 22,
  display: 'block',
  paddingBottom: '1rem',
  whiteSpace: 'nowrap',
  fontWeight: 600,

  [BREAKPOINTS.medium]: {
    paddingBottom: 0,
  },
});

const Text = styled.span({
  color: primaryColor(2),
  textAlign: 'center',
  display: 'block',
  width: '100%',
  whiteSpace: 'nowrap',
  fontWeight: 400,
});

const FleetGetAQuoteButton = styled(GetAQuoteButton)({
  backgroundColor: primaryColor(11),
  borderColor: primaryColor(2),
  borderWidth: 2,
});

const ButtonContainer = styled(View)({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  width: '100%',

  [BREAKPOINTS.small]: {
    justifyContent: 'center',
  },

  [BREAKPOINTS.medium]: {
    justifyContent: 'flex-end',
  },

  '& button, a': {
    fontSize: 16,
    whiteSpace: 'nowrap',
    margin: unitTRBL(0, 1, 2, 1),

    [BREAKPOINTS.verySmallOnly]: {
      fontSize: '0.8em',
    },

    [BREAKPOINTS.medium]: {
      fontSize: 18,
      margin: unitTRBL(0, 2, 0, 2),
    },
  },
});

interface GetAQuoteStickyBannerProps {
  salesforceCampaignId?: string;
  onClickAction: () => void;

  /** the HTML element ID you gave the element you want to start showing the sticky banner on */
  elementIdToShowOn: string;
}

/**
 * @param elementIdToShowOn - the HTML element ID you gave the element you want to start showing the sticky banner on
 * @returns sticky banner
 */
export default function FleetBrokerGetAQuoteStickyBanner({
  onClickAction,
  salesforceCampaignId,
  elementIdToShowOn,
}: GetAQuoteStickyBannerProps): JSX.Element {
  const isSmallDevice = useMediaQuery('(max-width: 1019px)');

  return (
    <StickyBanner
      elementIdToShowOn={elementIdToShowOn}
      testId="broker-sticky-banner"
    >
      <Container>
        <View>
          <Heading>Ready to partner with us?</Heading>
          {!isSmallDevice && (
            <Text>
              If you already work with us, speak to our experts to get a quote.
            </Text>
          )}
        </View>

        <ButtonContainer>
          <FleetBrokerPartnerWithUsButton
            salesforceCampaignId={salesforceCampaignId}
            css={{
              maxWidth: unit(32),
            }}
          />
          <FleetGetAQuoteButton
            onClickAction={onClickAction}
            salesforceCampaignId={salesforceCampaignId}
            trackEventString="Web: B2B Broker Landing Page - get a quote sticky button clicked"
          />
        </ButtonContainer>
      </Container>
    </StickyBanner>
  );
}
